<template>
  <div>
    <v-btn
      rounded="lg"
      class="gradient-color-primary"
      prepend-icon="mdi-merge"
      block
      @click="open = true"
      >Agenturen zusammenführen</v-btn
    >

    <v-dialog v-model="open" max-width="640">
      <v-card>
        <v-card-title>Agenturen zusammenführen</v-card-title>
        <v-card-text>
          <div class="mb-4">
            <div class="text-caption mb-1">Zielagentur</div>
            <code class="text-body-2">{{ agency?._id }}</code>
          </div>
          <v-text-field
            v-model="fromAgencyId"
            label="Quellagentur-ID"
            placeholder="24-stellige ObjectId"
            maxlength="24"
            counter
            :disabled="loading"
            required
            @input="checkSlotAvailability"
          />
          <v-alert
            v-if="slotCheckLoading"
            type="info"
            class="mt-2"
            density="compact"
            >Prüfe Slot-Verfügbarkeit...</v-alert
          >
          <v-alert
            v-if="slotInfo && slotInfo.canMerge"
            type="success"
            class="mt-2"
            density="compact"
            >{{ slotInfo.message }}</v-alert
          >
          <v-alert
            v-if="slotInfo && !slotInfo.canMerge"
            type="error"
            class="mt-2"
            density="compact"
            >{{ slotInfo.message }}</v-alert
          >
          <div v-if="slotInfo" class="mt-2 text-caption">
            <div>
              <strong>Quellagentur:</strong> {{ slotInfo.sourceAgency.activeCustomers }} aktive Kunden
            </div>
            <div>
              <strong>Zielagentur:</strong> {{ slotInfo.targetAgency.totalSlots }} Slots total,
              {{ slotInfo.targetAgency.activeCustomers }} aktiv,
              {{ slotInfo.targetAgency.archivedCustomers }} archiviert,
              {{ slotInfo.targetAgency.freeSlots }} frei
            </div>
            <div v-if="slotInfo.afterMerge.slotShortage > 0" class="text-error">
              <strong>Nach Merge:</strong> {{ slotInfo.afterMerge.totalActiveCustomers }} aktive Kunden
              ({{ slotInfo.afterMerge.customersWillBeInactive }} würden inaktiv gesetzt)
            </div>
            <div v-else class="text-success">
              <strong>Nach Merge:</strong> {{ slotInfo.afterMerge.totalActiveCustomers }} aktive Kunden
            </div>
          </div>
          <v-switch
            v-model="dryRun"
            :disabled="loading"
            label="Trockenlauf (nur Zähler anzeigen)"
          />
          <v-switch
            v-model="deleteSource"
            :disabled="loading"
            label="Quellagentur nach Zusammenführung löschen"
          />
          <v-switch
            v-model="mergePricingInfo"
            :disabled="loading"
            label="Quellagentur-PricingInfo löschen (wenn beide existieren)"
            hint="PricingInfo wird nie zusammengeführt. Wenn beide Agenturen PricingInfo haben, wird die Quellagentur-PricingInfo gelöscht (Zielagentur behält ihre PricingInfo)"
            persistent-hint
          />
          <v-switch
            v-model="deleteRemoteResources"
            :disabled="loading"
            label="Remote-Ressourcen löschen (Stripe, S3-Blobs)"
            color="warning"
            hint="⚠️ Löscht Stripe-Abonnements und S3-Dateien der Quellagentur"
            persistent-hint
          />
          <v-alert
            v-if="deleteRemoteResources"
            type="warning"
            class="mt-2"
            density="compact"
            >⚠️ Achtung: Diese Aktion löscht Stripe-Abonnements und S3-Dateien
            permanent!</v-alert
          >

          <v-alert v-if="error" type="error" class="mt-2" density="compact">{{
            error
          }}</v-alert>
          <v-alert
            v-if="success"
            type="success"
            class="mt-2"
            density="compact"
            >{{ success }}</v-alert
          >
          <pre v-if="result" class="result-pre mt-2">{{ result }}</pre>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="resetAndClose" :disabled="loading"
            >Schließen</v-btn
          >
          <v-btn
            color="primary"
            :loading="loading"
            :disabled="!canProceedWithMerge"
            @click="runMerge"
            >{{
            dryRun ? "Trockenlauf starten" : "Zusammenführung ausführen"
          }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import apiClient from "../../plugins/axios";

export default {
  name: "MergeAgencies",
  props: {
    agency: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      open: false,
      fromAgencyId: "",
      dryRun: true,
      deleteSource: false,
      deleteRemoteResources: false,
      mergePricingInfo: false,
      loading: false,
      error: "",
      success: "",
      result: "",
      slotInfo: null,
      slotCheckLoading: false,
      slotCheckTimeout: null,
    };
  },
  watch: {
    dryRun() {
      // Re-check slots when dry run toggle changes
      if (this.fromAgencyId && this.fromAgencyId.length === 24) {
        this.checkSlotAvailability();
      }
    },
  },
  beforeUnmount() {
    // Clean up timeout
    if (this.slotCheckTimeout) {
      clearTimeout(this.slotCheckTimeout);
    }
  },
  computed: {
    canProceedWithMerge() {
      // Can proceed if:
      // 1. Valid agency IDs are provided
      // 2. Either slot check passed (canMerge === true) OR dry run is enabled (allows checking)
      // 3. Not currently loading
      if (
        !this.agency?._id ||
        !this.fromAgencyId ||
        this.fromAgencyId.length !== 24 ||
        this.loading
      ) {
        return false;
      }
      // Allow dry run even if slots are insufficient (for testing)
      if (this.dryRun) {
        return true;
      }
      // For actual merge, require slot check to pass
      return this.slotInfo === null || this.slotInfo.canMerge;
    },
  },
  methods: {
    resetAndClose() {
      if (this.slotCheckTimeout) {
        clearTimeout(this.slotCheckTimeout);
        this.slotCheckTimeout = null;
      }
      this.open = false;
      this.fromAgencyId = "";
      this.dryRun = true;
      this.deleteSource = false;
      this.deleteRemoteResources = false;
      this.mergePricingInfo = false;
      this.loading = false;
      this.error = "";
      this.success = "";
      this.result = "";
      this.slotInfo = null;
      this.slotCheckLoading = false;
    },
    async checkSlotAvailability() {
      // Reset slot info when input changes
      this.slotInfo = null;
      this.error = "";

      // Only check if we have valid IDs
      if (
        !this.agency?._id ||
        !this.fromAgencyId ||
        this.fromAgencyId.length !== 24
      ) {
        return;
      }

      // Debounce: wait a bit before checking
      if (this.slotCheckTimeout) {
        clearTimeout(this.slotCheckTimeout);
      }

      this.slotCheckTimeout = setTimeout(async () => {
        try {
          this.slotCheckLoading = true;
          const { data } = await apiClient.post(
            "/actions/mergeAgencies/checkSlots",
            {
              fromAgencyId: this.fromAgencyId,
              toAgencyId: this.agency._id,
            }
          );
          this.slotInfo = data;
        } catch (e) {
          // Don't show error for slot check failures, just don't show slot info
          console.warn("Slot check failed:", e);
          this.slotInfo = null;
        } finally {
          this.slotCheckLoading = false;
        }
      }, 500); // 500ms debounce
    },
    async runMerge() {
      this.error = "";
      this.success = "";
      this.result = "";
      if (
        !this.agency?._id ||
        !this.fromAgencyId ||
        this.fromAgencyId.length !== 24
      ) {
        this.error = "Gib eine gültige 24-stellige Quellagentur-ID ein.";
        return;
      }
      try {
        this.loading = true;
        const payload = {
          fromAgencyId: this.fromAgencyId,
          toAgencyId: this.agency._id,
          dryRun: this.dryRun,
          deleteSource: this.deleteSource,
          deleteRemoteResources: this.deleteRemoteResources,
          mergePricingInfo: this.mergePricingInfo,
        };
        const { data } = await apiClient.post(
          "/actions/mergeAgencies",
          payload
        );
        this.result = JSON.stringify(data, null, 2);
        this.success = this.dryRun
          ? "Trockenlauf abgeschlossen."
          : "Zusammenführung durchgeführt.";
        // Re-check slots after merge to show updated status
        if (!this.dryRun && this.fromAgencyId) {
          await this.checkSlotAvailability();
        }
      } catch (e) {
        this.error =
          e?.response?.data?.error || e?.message || "Unbekannter Fehler";
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.result-pre {
  max-height: 300px;
  overflow: auto;
  background: #0a0a0a;
  color: #e5e7eb;
  padding: 8px;
  border-radius: 8px;
}
</style>
